import React, { useMemo } from "react";
import { Box, Center, Divider, Flex, Hide, Show, Spinner, useMediaQuery } from "@chakra-ui/react";
import { TFunction } from "i18next";
import { useAppSelector } from "../../../app/hooks";
import { selectAppState } from "../../../app/slice/appSlice";
import { AnalyticsRecord, selectVariantAnalysis } from "../../../app/slice/analyticsSlice";
import { useTranslation } from "react-i18next";
import AnalyticsRow from "./AnalyticsRow";
import { Icons, Typography, useCurrencyFormatter } from "@reconvert/react-ui-component";
import useIsActiveOnPage, { PagePurpose } from "../../../hooks/useIsActiveOnPage";

interface VariantAnalyticsRow {
  name: string;
  type: "revenue" | "impressions" | "conversions" | "declined" | "cvr" | "click";
  currentValue: string;
  diffInPercentage: string;
}

export const prepareVariantAnalyticsRow = (
  t: TFunction<"translation", undefined>,
  isAnalyticsDeclineOffers: boolean,
  variantsAnalyticDetail?: AnalyticsRecord,
): VariantAnalyticsRow[] =>
  isAnalyticsDeclineOffers
    ? [
        {
          name: t("Revenue"),
          type: "revenue",
          currentValue: variantsAnalyticDetail?.revenue?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.revenueDiffPercentage?.toString() ?? "0",
        },
        {
          name: t("Impressions"),
          type: "impressions",
          currentValue: variantsAnalyticDetail?.impressions?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.impressionsDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Conversions"),
          type: "conversions",
          currentValue: variantsAnalyticDetail?.conversions?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.conversionsDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Declined offers"),
          type: "declined",
          currentValue: variantsAnalyticDetail?.declined?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.declinedDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Conversion rate"),
          type: "cvr",
          currentValue: (variantsAnalyticDetail?.cvr?.toString() ?? "0") + "%",
          diffInPercentage: variantsAnalyticDetail?.cvrDiffPercentage?.toString() ?? "0%",
        },
      ]
    : [
        {
          name: t("Revenue"),
          type: "revenue",
          currentValue: variantsAnalyticDetail?.revenue?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.revenueDiffPercentage?.toString() ?? "0",
        },
        {
          name: t("Impressions"),
          type: "impressions",
          currentValue: variantsAnalyticDetail?.impressions?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.impressionsDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Clicks"),
          type: "click",
          currentValue: variantsAnalyticDetail?.clicks?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.clicksDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Conversions"),
          type: "conversions",
          currentValue: variantsAnalyticDetail?.conversions?.toString() ?? "0",
          diffInPercentage: variantsAnalyticDetail?.conversionsDiffPercentage?.toString() ?? "0%",
        },
        {
          name: t("Conversion rate"),
          type: "cvr",
          currentValue: (variantsAnalyticDetail?.cvr?.toString() ?? "0") + "%",
          diffInPercentage: variantsAnalyticDetail?.cvrDiffPercentage?.toString() ?? "0%",
        },
      ];

const AnalyticView = ({ slotId, variantStatus }: { slotId: string; variantStatus: boolean }) => {
  const [isLargerThan1464] = useMediaQuery("(min-width: 1464px)");
  const [isLargerThan1573] = useMediaQuery("(min-width: 1573px)");
  const { t } = useTranslation();
  const {
    view: { isAnalyticView },
    activePage,
    storeInfo,
  } = useAppSelector(selectAppState);
  const { variantsAnalytics, loading, error } = useAppSelector(selectVariantAnalysis);
  const { currencyFormatter } = useCurrencyFormatter();
  const isAnalyticsDeclineOffers = useIsActiveOnPage(PagePurpose.ANALYTICS_DECLINE_OFFERS, activePage!);

  
  const analyticsDetails = useMemo(() => {
    const variantsAnalyticDetail = variantsAnalytics.filter(
      (analytics: AnalyticsRecord) => analytics.slotId === slotId,
    )[0];

    if (variantsAnalyticDetail) {
      return prepareVariantAnalyticsRow(t, isAnalyticsDeclineOffers, variantsAnalyticDetail);
    } else {
      return null;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantsAnalytics, slotId, activePage]);

  let impressionValue = Number.parseFloat(analyticsDetails?.find(e => e.type === "impressions")?.currentValue || "0");

  return (
    <Box>
      {isAnalyticView ? (
        <>
          <Divider mt={3} />
          {loading ? (
            <Center pt={2} pb={2}>
              <Spinner variant={"ring"} size={"sm"} />
            </Center>
          ) : (
            <>
              {!error && analyticsDetails && impressionValue > 0 ? (
                <Flex py={3} gap={6} width={"100%"}>
                  <Flex pr={1} justifyContent={"space-between"} width={"50%"} pt={2} pb={2}>
                    {analyticsDetails?.slice(1)?.map((val: VariantAnalyticsRow, index: number) => (
                      <>
                        <AnalyticsRow
                          key={index}
                          name={val?.name}
                          type={val?.type}
                          value={val?.currentValue}
                          change={val?.diffInPercentage}
                          variantStatus={variantStatus}
                          iconSize={"sm"}
                          layout="newLayout"
                        />
                      </>
                    ))}
                  </Flex>
                  <Flex
                    py={"8px"}
                    borderRadius={"8px"}
                    backgroundColor={"#F4F6F8"}
                    direction="column"
                    width={"50%"}
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <Typography variant="body-16-regular">
                      {t("Revenue")}
                    </Typography>
                    {analyticsDetails?.[0]?.currentValue ? (
                      <>
                        <Flex justifyContent={"center"} alignItems={"center"} gap={"6px"}>
                          <Typography variant="body-16-regular">
                            {currencyFormatter(+analyticsDetails?.[0]?.currentValue, storeInfo?.currency!)}
                          </Typography>
                          <Typography
                            color={
                              variantStatus
                                ? analyticsDetails?.[0]?.diffInPercentage.startsWith("-")
                                  ? `error.800`
                                  : `success.800`
                                : "disable.color"
                            }
                            variant="xsmall"
                          >
                            {String(analyticsDetails?.[0]?.diffInPercentage)?.startsWith("-")
                              ? analyticsDetails?.[0]?.diffInPercentage
                              : `+${analyticsDetails?.[0]?.diffInPercentage}`}
                          </Typography>
                        </Flex>
                      </>
                    ) : (
                      <Typography variant="xsmall" fontWeight={500}>
                        -
                      </Typography>
                    )}
                  </Flex>
                </Flex>
              ) : (
                <Typography
                  my={3}
                  textAlign="center"
                  variant="body-16-regular"
                  color={error ? "error.700" : variantStatus ? "neutrals.500" : "disable.color"}
                >
                  {error
                    ? t("It seems we're having trouble fetching the Analytics information you requested")
                    : t("Your analytics will be displayed here as soon as data is available")}
                </Typography>
              )}
            </>
          )}
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default AnalyticView;
